/**
 * @file Travel host taxonomies and the checklist label table.
 *
 * Every whitelisted value here mirrors the API contract in
 * docs/travel-host-onboarding-frontend-guide.md (§5 specialties, §6 credential
 * types, §7 missing_fields). Sending a value that is not in these lists is a
 * 400, so this file is the client-side guard rail: a picker built from
 * SPECIALTIES[hostType] makes a cross-type value structurally unreachable
 * rather than merely validated against.
 *
 * MISSING_FIELD_MAP turns a server checklist key into a renderable row. It does
 * NOT decide what is missing — the server owns that, and the client never
 * recomputes it (plan §7.2 invariant 1). This table only supplies the label,
 * the one-line fix microcopy and the deep link for a key the server already
 * sent.
 *
 * Plain .js: no JSX, per the utils/guide-url.js precedent.
 */

// ---------------------------------------------------------------------------
// Host types
// ---------------------------------------------------------------------------

/**
 * `other` is a valid host_type in the database for manually created rows, but
 * signup refuses it and there is no wizard for it (guide §3a). It is
 * deliberately absent here — see plan risk R10.
 */
export const HOST_TYPES = {
  MEDICAL_PROVIDER: 'medical_provider',
  TOUR_GUIDE: 'tour_guide'
};

export const HOST_TYPE_LABELS = {
  medical_provider: 'Medical provider',
  tour_guide: 'Tour guide'
};

// ---------------------------------------------------------------------------
// Specialties — guide §5. The two lists are disjoint and enforced server-side.
// ---------------------------------------------------------------------------

const MEDICAL_SPECIALTIES = [
  { value: 'dentistry', label: 'Dentistry' },
  { value: 'cosmetic_surgery', label: 'Cosmetic surgery' },
  { value: 'orthopedics', label: 'Orthopedics' },
  { value: 'dermatology', label: 'Dermatology' },
  { value: 'fertility', label: 'Fertility' },
  { value: 'ophthalmology', label: 'Ophthalmology' },
  { value: 'cardiology', label: 'Cardiology' },
  { value: 'oncology', label: 'Oncology' },
  { value: 'wellness_checkup', label: 'Wellness checkup' },
  { value: 'other', label: 'Other' }
];

const GUIDE_SPECIALTIES = [
  { value: 'food', label: 'Food' },
  { value: 'history', label: 'History' },
  { value: 'culture', label: 'Culture' },
  { value: 'adventure', label: 'Adventure' },
  { value: 'photography', label: 'Photography' },
  { value: 'nightlife', label: 'Nightlife' },
  { value: 'nature', label: 'Nature' },
  { value: 'other', label: 'Other' }
];

/** Keyed by host_type. Read as SPECIALTIES[hostType] — never concatenate the two. */
export const SPECIALTIES = {
  medical_provider: MEDICAL_SPECIALTIES,
  tour_guide: GUIDE_SPECIALTIES
};

// ---------------------------------------------------------------------------
// Credential types — guide §6
// ---------------------------------------------------------------------------

export const CREDENTIAL_TYPES = [
  { value: 'medical_license', label: 'Medical licence' },
  { value: 'board_certification', label: 'Board certification' },
  { value: 'facility_accreditation', label: 'Facility accreditation' },
  { value: 'malpractice_insurance', label: 'Malpractice insurance' },
  { value: 'guide_license', label: 'Guide licence' },
  { value: 'first_aid_cert', label: 'First aid certificate' },
  { value: 'government_id', label: 'Government ID' },
  { value: 'other', label: 'Other' }
];

export const CREDENTIAL_TYPE_LABELS = Object.fromEntries(CREDENTIAL_TYPES.map((type) => [type.value, type.label]));

const byValue = (values) => values.map((value) => CREDENTIAL_TYPES.find((type) => type.value === value));

/**
 * The same eight types, ordered with the host type's most relevant first, so
 * the Select in the credential editor opens on something plausible (plan §3.5).
 * Every type stays selectable for both host types — the API does not restrict
 * them, and a guide may well hold a government ID or a malpractice policy.
 */
export const CREDENTIAL_TYPES_BY_HOST = {
  medical_provider: byValue([
    'medical_license',
    'board_certification',
    'facility_accreditation',
    'malpractice_insurance',
    'government_id',
    'other'
  ]),
  tour_guide: byValue(['guide_license', 'first_aid_cert', 'government_id', 'other'])
};

// ---------------------------------------------------------------------------
// Checklist rows — plan §4
// ---------------------------------------------------------------------------

/**
 * Group keys used by MISSING_FIELD_MAP rows. HUB_SECTIONS below carries the
 * rendered heading for each, so the copy lives in one place and the rows stay
 * short.
 */
export const CHECKLIST_SECTIONS = {
  ACCOUNT: 'Account',
  PROFILE: 'Profile',
  CREDENTIALS: 'Credentials',
  REVIEW: 'Review'
};

/** Render order and headings for the hub (plan §3.7 wireframe). */
export const HUB_SECTIONS = [
  { key: CHECKLIST_SECTIONS.ACCOUNT, heading: 'Account and phone' },
  { key: CHECKLIST_SECTIONS.PROFILE, heading: 'Your profile' },
  { key: CHECKLIST_SECTIONS.CREDENTIALS, heading: 'Credentials' },
  { key: CHECKLIST_SECTIONS.REVIEW, heading: 'Review and submit' }
];

const PROFILE_IDENTITY = '/host/onboarding/profile#identity';
const PROFILE_ABOUT = '/host/onboarding/profile#about';
const PROFILE_LOCATION = '/host/onboarding/profile#location';
const PROFILE_PRACTICE = '/host/onboarding/profile#practice';
const PROFILE_EXPERIENCE = '/host/onboarding/profile#experience';

export const MISSING_FIELD_MAP = {
  first_name: {
    section: CHECKLIST_SECTIONS.ACCOUNT,
    label: 'Your first name',
    detail: 'As it appears on your licence or ID.',
    to: PROFILE_IDENTITY
  },
  last_name: {
    section: CHECKLIST_SECTIONS.ACCOUNT,
    label: 'Your last name',
    detail: "Google didn't give us this one — please add it.",
    to: PROFILE_IDENTITY
  },
  phone_verification: {
    section: CHECKLIST_SECTIONS.ACCOUNT,
    label: 'Verify your phone number',
    detail: "We'll text a six-digit code. Takes about a minute.",
    to: '/host/onboarding/phone'
  },
  display_name: {
    section: CHECKLIST_SECTIONS.PROFILE,
    label: 'Your practice or public name',
    detail: 'The headline of your listing — a practice name, or your own.',
    to: PROFILE_IDENTITY
  },
  biography: {
    section: CHECKLIST_SECTIONS.PROFILE,
    label: 'A short about section',
    detail: 'Two or three sentences on who you treat and how you work.',
    to: PROFILE_ABOUT
  },
  languages: {
    section: CHECKLIST_SECTIONS.PROFILE,
    label: 'Languages you work in',
    detail: 'Pick every language you can hold a consultation in.',
    to: PROFILE_ABOUT
  },
  specialties: {
    section: CHECKLIST_SECTIONS.PROFILE,
    label: 'Your specialities',
    detail: 'Choose at least one. Only the ones listed apply to your host type.',
    to: PROFILE_ABOUT
  },
  base_location_id: {
    section: CHECKLIST_SECTIONS.PROFILE,
    label: "Where you're based",
    detail: 'Search for your clinic or hospital and pick it from the list — we need the real place, not a typed address.',
    to: PROFILE_LOCATION
  },
  // Guides only.
  years_experience: {
    section: CHECKLIST_SECTIONS.PROFILE,
    label: 'Years guiding',
    detail: 'Enter 0 if this is your first season — zero is a valid answer.',
    to: PROFILE_EXPERIENCE
  },
  // Medical providers only.
  provider_kind: {
    section: CHECKLIST_SECTIONS.PROFILE,
    label: 'Independent or clinic',
    detail: "Tells patients whether they're booking you or a facility.",
    to: PROFILE_PRACTICE
  },
  professional_title: {
    section: CHECKLIST_SECTIONS.PROFILE,
    label: 'Your professional title',
    detail: 'MD, DDS, RN — whatever appears after your name.',
    to: PROFILE_PRACTICE
  },
  clinic_affiliation: {
    section: CHECKLIST_SECTIONS.PROFILE,
    label: 'Clinic or hospital affiliation',
    detail: 'Where you practise. Write "Independent" if you have none.',
    to: PROFILE_PRACTICE
  },
  consultation_modes: {
    section: CHECKLIST_SECTIONS.PROFILE,
    label: 'How you consult',
    detail: 'In person, telehealth, or both.',
    to: PROFILE_PRACTICE
  },
  medical_license: {
    section: CHECKLIST_SECTIONS.CREDENTIALS,
    label: 'A complete medical licence',
    detail: "A licence only counts with BOTH a number and an issuing authority. A half-filled row silently doesn't count.",
    to: '/host/onboarding/credentials?add=medical_license'
  }
};

/**
 * `base_location_id` -> `Base location id`. Good enough for a key we have no
 * copy for, and for the live region's diff sentence, where the row label
 * ("Verify your phone number") would read as an instruction rather than an
 * announcement — "Phone verification complete." is what we want there.
 */
export const humaniseKey = (key) => {
  const words = String(key).replace(/[_-]+/g, ' ').trim();
  return `${words.charAt(0).toUpperCase()}${words.slice(1)}`;
};

/**
 * Row for a checklist key the frontend has no label for.
 *
 * The backend can add a submit requirement before this table ships a row for
 * it. Without this fallback the host would see a permanently disabled Submit
 * button and no explanation — an invisible blocker. With it they get a named
 * row and a route, which is recoverable.
 */
export const fallbackRow = (key) => ({
  section: CHECKLIST_SECTIONS.REVIEW,
  label: humaniseKey(key),
  detail: 'We need one more detail here. Open the review page to see it in context.',
  to: '/host/onboarding/review'
});

/** The row for a server checklist key, always defined. */
export const checklistRow = (key) => MISSING_FIELD_MAP[key] ?? fallbackRow(key);

// ---------------------------------------------------------------------------
// The cosmetic denominator — plan §3.7
// ---------------------------------------------------------------------------

/**
 * `phone_verification` is deliberately absent: the backend's
 * REQUIRE_PHONE_VERIFICATION flag is off, so the server never asks for it and
 * counting it would make every host read "7 of 8 done" forever.
 *
 * Its MISSING_FIELD_MAP row above stays regardless — the hub renders whatever
 * the server sends, so if the flag is turned back on the row works immediately
 * and only this list needs the key added back.
 */
const COMMON_CHECKLIST_KEYS = ['first_name', 'last_name', 'display_name', 'biography', 'languages', 'specialties', 'base_location_id'];

/**
 * What each host type is *expected* to satisfy.
 *
 * COSMETIC ONLY, and stated as such because it is the one table in this file
 * that could be mistaken for a rule. It exists so the hub can render
 * "4 of 13 done" and "all 6 items complete" — a denominator, nothing else.
 * The server owns completeness (plan §7.2 invariant 1) and the only gate
 * anywhere is `missing_fields.length === 0`.
 *
 * If this list ever drifts from the server's rules the consequence is a
 * slightly wrong-looking fraction, never a wrong gate: the hub takes the union
 * of this list and the server's `missing_fields`, so an unmapped server
 * requirement widens the denominator instead of producing a negative count.
 */
export const CHECKLIST_KEYS_BY_HOST_TYPE = {
  medical_provider: [
    ...COMMON_CHECKLIST_KEYS,
    'provider_kind',
    'professional_title',
    'clinic_affiliation',
    'consultation_modes',
    'medical_license'
  ],
  tour_guide: [...COMMON_CHECKLIST_KEYS, 'years_experience']
};

/** Expected keys for a host type. Unknown types fall back to the shared set. */
export const checklistKeysFor = (hostType) => CHECKLIST_KEYS_BY_HOST_TYPE[hostType] ?? COMMON_CHECKLIST_KEYS;
